import { useEffect } from "react";
import { Paper, Box, Typography } from "@mui/material";
import useTrainDashboardMonitor from "../../app/hooks/useTrainDashboardMonitor";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import GroupsIcon from "@mui/icons-material/Groups";
import EmergencyIcon from "@mui/icons-material/Emergency";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CampaignIcon from "@mui/icons-material/Campaign";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import { Gauge } from "@mui/x-charts"; import {
    GaugeContainer,
    GaugeReferenceArc,
    GaugeValueArc,
    useGaugeState
} from "@mui/x-charts/Gauge";
const ALERT_ICONS = {
    emergencia: EmergencyIcon,
    bypass: EngineeringIcon,
    acionamentoEmergencia: CampaignIcon,
    portas: DoorFrontIcon,
};
import WifiOffIcon from "@mui/icons-material/WifiOff";
import PortableWifiOffIcon from "@mui/icons-material/PortableWifiOff";
import Tooltip from "@mui/material/Tooltip";
export default function TrainCard({
    train,
    setTrainSelected,
    setLoadingTrain,
    onLiveUpdate,
}) {

    // Assina o mesmo SSE por trem usado dentro do trem (com fallback automático
    // pra polling se o SSE falhar/travar — problema conhecido em algumas máquinas).
    // Não usamos o snapshot em si aqui, só o timestamp de "chegou dado novo" como
    // gatilho pra reconsultar /api/telemetry/alerts (a fonte real do status do card).
    const { data: liveSnapshot } = useTrainDashboardMonitor(train.id);

    useEffect(() => {
        if (liveSnapshot && Object.keys(liveSnapshot).length > 0) {
            onLiveUpdate?.();
        }
    }, [liveSnapshot, onLiveUpdate]);

    const activeRules = Object.entries(train.alerts || {})
        .filter(([_, alert]) => alert.active)
        .map(([key, alert]) => ({
            key,
            ...alert
        }));

    const hasCritical = activeRules.some(
        r => r.level === "critical"
    );

    const hasWarning = activeRules.some(
        r => r.level === "warning"
    );
    const formatTelemetryAge = (totalSeconds) => {
        const totalMinutes = Math.floor(totalSeconds / 60);
        const days = Math.floor(totalMinutes / 1440);
        const hours = Math.floor((totalMinutes % 1440) / 60);
        const minutes = totalMinutes % 60;

        if (days >= 1) {
            return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
        }
        if (hours >= 1) {
            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
        }
        return `${minutes}m`;
    };
    const telemetryAge =
        train.segundosSemTelemetria ?? '-';

    const telemetryWarning =
        telemetryAge >= 300 &&
        telemetryAge < 600;

    const telemetryCritical =
        telemetryAge >= 600;
    const severityLabel = {
        warning: "Atenção",
        critical: "Crítica"
    };
    const getCategory = (status) => {
        const statusCard = (status || "")
            .toString()
            .trim()
            .toLowerCase();

        if ([
            "operacional",
            "em operação",
            "em operacao"
        ].includes(statusCard)) {
            return "normal";
        }

        if ([
            "erro",
            "instável",
            "instavel",
            "degradado"
        ].includes(statusCard)) {
            return "degradado";
        }

        if ([
            "manutenção",
            "manutencao"
        ].includes(statusCard)) {
            return "manutencao";
        }

        if ([
            "desligado",
            "parado"
        ].includes(statusCard)) {
            return "desligado";
        }

        if ([
            "inoperante",
            "crítico",
            "critico"
        ].includes(statusCard)) {
            return "critico";
        }

        return "normal";
    };

    let category = getCategory(train.status);
    if (telemetryCritical && category !== "manutencao") {
        category = "degradado";
    }
    // ALERTAS SOBRESCREVEM STATUS
    if (hasCritical) {
        category = "critico";
    } else if (hasWarning) {
        category = "degradado";
    }

    const titleText = {
        normal: "Operacional",
        degradado: "Operando com restrições",
        critico: "Alarme Critico",
        manutencao: "Em manutenção",
        desligado: "Desligado"
    }[category] || "Operacional";

    const statusColor = {
        normal: "#22c55e",       // Verde
        degradado: "#eab308",    // Amarelo
        critico: "#ef4444",      // Vermelho
        manutencao: "#f97316",   // Laranja
        desligado: "#9ca3af"     // Cinza
    }[category] || "#22c55e";

    const handleClick = () => {
        if (category === "manutencao") {
            alert("🚧 Este trem está em manutenção.");
            return;
        }

        setLoadingTrain(true);
        setTrainSelected(train.id);
    };

    const isMultiLine = activeRules.length > 3;
    function MiniSpeedometerText() {

        const { value } = useGaugeState();

        return (
            <>
                <text
                    x="50%"
                    y="58%"
                    textAnchor="middle"
                    dominantBaseline="middle"

                    style={{
                        fontSize: 12,
                        fontWeight: 800,
                        fill: "#374151"
                    }}
                >
                    {Math.round(value || 0)}
                </text>

                <text
                    x="50%"
                    y="78%"
                    textAnchor="middle"
                    dominantBaseline="middle"

                    style={{
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: 1,
                        fill: "#6b7280"
                    }}
                >
                    KM/H
                </text>
            </>
        );
    }
    return (
        <Paper
            onClick={handleClick}
            elevation={0}
            sx={{
                position: "relative",
                overflow: "hidden",

                display: "flex",
                alignItems: "center",
                gap: 2,

                padding: "10px 14px",

                borderRadius: "14px",
                cursor: "pointer",

                background: "#e9e6f2",
                width: "100%",

                flexDirection: "column",
                alignItems: "flex-start",

                "&:hover": {
                    boxShadow: "0 6px 14px rgba(0,0,0,0.08)"
                }
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    gap: "1px",
                    width: "100%"
                }}
            >

                {/* CÍRCULO */}
                <Box sx={{ position: "relative" }}>

                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: "50%",

                            background: "#dcd6f7",

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            fontWeight: 700,
                            fontSize: 14
                        }}
                    >
                        {train.name}
                    </Box>

                    {/* STATUS DOT */}
                    <Box
                        sx={{
                            position: "absolute",

                            bottom: isMultiLine ? 2 : 1,
                            left: "82%",

                            transform: "translateX(-50%)",

                            width:
                                category === "critico"
                                    ? 10
                                    : category === "degradado"
                                        ? 9
                                        : 8,

                            height:
                                category === "critico"
                                    ? 10
                                    : category === "degradado"
                                        ? 9
                                        : 8,

                            boxShadow:
                                category === "critico"
                                    ? "0 0 6px #ef4444"
                                    : category === "degradado"
                                        ? "0 0 5px #eab308"
                                        : "none",

                            borderRadius: "50%",
                            background: statusColor
                        }}
                    />

                </Box>

                {/* TEXTO + ALERTAS */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        minHeight: 36
                    }}
                >
                    {/* ÍCONES */}
                    <Box
                        sx={{
                            justifyContent: "center",
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                            flex: 1
                        }}
                    >
                        {activeRules.map(rule => {

                            const IconComponent =
                                ALERT_ICONS[rule.key];

                            return (
                                <Box
                                    key={rule.key}
                                    sx={{
                                        position: "relative",

                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    {IconComponent && (
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title={
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: 12
                                                        }}
                                                    >
                                                        {rule.label}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11
                                                        }}
                                                    >
                                                        Categoria: {rule.key}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11
                                                        }}
                                                    >
                                                        Severidade: {rule.level}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: 11
                                                        }}
                                                    >
                                                        Valor: {rule.value}
                                                    </Typography>
                                                </Box>
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    cursor: "help"
                                                }}
                                            >
                                                <IconComponent
                                                    fontSize="small"
                                                    sx={{
                                                        color: rule.color,
                                                        zIndex: 1
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* VELOCÍMETRO */}
                    {/* VELOCÍMETRO / ALERTA TELEMETRIA */}
                    <Box
                        sx={{
                            minWidth: 55,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        {telemetryCritical ? (

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center"
                                }}
                            >
                                <WifiOffIcon
                                    sx={{
                                        color: "#ef4444",
                                        fontSize: 28,
                                        filter:
                                            "drop-shadow(0 0 6px rgba(239,68,68,.8))"
                                    }}
                                />

                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#ef4444",
                                        fontWeight: 700,
                                        lineHeight: 1
                                    }}
                                >
                                    {formatTelemetryAge(telemetryAge)}
                                </Typography>
                            </Box>

                        ) : telemetryWarning ? (

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center"
                                }}
                            >
                                <PortableWifiOffIcon
                                    sx={{
                                        color: "#f59e0b",
                                        fontSize: 26
                                    }}
                                />

                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#f59e0b",
                                        fontWeight: 700,
                                        lineHeight: 1
                                    }}
                                >
                                    {Math.floor(telemetryAge / 60)}m
                                </Typography>
                            </Box>

                        ) : (

                            <GaugeContainer
                                width={55}
                                height={40}
                                value={train.speed || 0}
                                valueMin={0}
                                valueMax={120}
                                startAngle={-110}
                                endAngle={110}
                            >
                                <GaugeReferenceArc
                                    sx={{
                                        fill: "#d1d5db"
                                    }}
                                />

                                <GaugeValueArc
                                    sx={{
                                        fill: "#22c55e",
                                        transition: "all .3s ease"
                                    }}
                                />

                                <MiniSpeedometerText />
                            </GaugeContainer>

                        )}
                    </Box>
                </Box>

            </Box>

            {/* BARRA INFERIOR */}
            <Box
                sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,

                    height: "6px",

                    background: statusColor
                }}
            />

        </Paper>
    );
}